import { HgSegmentedOptions, HgSelectOptions } from '@smg-real-estate/domus';
import { computed, onMounted, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import adaptPriceTrendsData, {
  PriceTrendsChartDataItem,
} from '@/components/local-landing-page/PriceDevelopment/adaptPriceTrendsData';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { PriceEvolutionDataItem, PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { PriceTrendsRangeSummary, PropertyType } from '@/types';

const NO_DATA_MIN = 0;
const NO_DATA_MAX = 250;

export interface UsePriceTrendsOptions {
  selectedRange: Ref<number | string>;
  priceTrendsData: Ref<PriceEvolutionResponse | null>;
  propertyType: Ref<PropertyType>;
  onRangeChange?: (value: PriceTrendsRangeSummary, isInitial: boolean) => void;
}

export function usePriceTrends(options: UsePriceTrendsOptions) {
  const { t } = useI18n();

  // Reactive state
  const isMounted = ref(false);
  const isIs24 = isPlatformIs24();

  // Computed properties
  const priceTrendsAdaptData = computed(
    (): PriceTrendsChartDataItem =>
      adaptPriceTrendsData(options.priceTrendsData.value, +options.selectedRange.value, isIs24)[
        options.propertyType.value
      ]!,
  );

  const series = computed(() => {
    return priceTrendsAdaptData.value.series;
  });

  const evolutions = computed((): number[] => {
    return priceTrendsAdaptData.value.evolutions;
  });

  const noDataCategories = computed((): string[] => {
    return Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString()).reverse();
  });

  const categories = computed<any[]>((): string[] => {
    if (!series.value[0]?.data?.length) {
      return noDataCategories.value;
    }
    return priceTrendsAdaptData.value.categories;
  });

  const min = computed((): number => {
    if (!series.value[0]?.data?.length) {
      return NO_DATA_MIN;
    }
    return priceTrendsAdaptData.value.min;
  });

  const max = computed((): number => {
    if (!series.value[0]?.data?.length) {
      return NO_DATA_MAX;
    }
    return priceTrendsAdaptData.value.max;
  });

  const selections = computed((): HgSelectOptions[] | HgSegmentedOptions[] => {
    const priceTrendsData: PriceEvolutionDataItem[] =
      options.priceTrendsData?.value?.results?.[options.propertyType.value] ?? [];
    const len = priceTrendsData.length;
    if (len <= 2) return []; // returns empty array if results <= 2

    const selectionsSize = Math.floor(len / 2) + 1;
    return Array.from({ length: selectionsSize }, (_, index) => {
      if (index === 0) {
        return {
          value: isIs24 ? 0.5 : '0.5',
          text: t('localLanding.priceTrends.month'),
        };
      } else {
        return {
          value: isIs24 ? index : index.toString(),
          text:
            index === 1
              ? t('localLanding.priceTrends.year', {
                  value: index,
                })
              : t('localLanding.priceTrends.years', {
                  value: index,
                }),
        };
      }
    }).reverse();
  });

  // Methods
  const getXTextByIndex = (index = 0): string => {
    const xValue = categories.value[index];
    return (
      xValue ||
      t('localLanding.priceTrends.tooltip', {
        year: index === 0 ? categories.value[index + 1] - 1 : categories.value[index - 1],
      })
    );
  };

  const handleRangeChangeHg = (value: number, isInitial = false) => {
    if (evolutions.value.length === 0) return;

    const evolutionsLength = evolutions.value.length;
    const evolution = evolutions.value[evolutionsLength - 1];

    handleRangeChange(value, evolution, isInitial);
  };

  const handleRangeChangeIs24 = (value: number, isInitial = false) => {
    if (series.value[0].data.length === 0) return;

    const s = series.value[0].data;
    const sLength = s.length;
    const evolution = s[sLength - 1] as number;

    handleRangeChange(value, evolution, isInitial);
  };

  const handleRangeChange = (value: number, evolution: number, isInitial = false) => {
    const params: PriceTrendsRangeSummary = {
      range: value || +options.selectedRange.value,
      year: getXTextByIndex(),
      evolution: evolution.toString(),
      evolutionType: evolution >= 0 ? 'increase' : 'decrease',
    };

    options.onRangeChange?.(params, isInitial);
  };

  const handleUpdatedRangeHg = (value: any) => {
    handleRangeChangeHg(value);
  };

  const handleUpdatedRangeIs24 = (value: any) => {
    handleRangeChangeIs24(value);
  };

  // Lifecycle
  onMounted(() => {
    isMounted.value = true; // For ApexChart is only supported on client side
  });

  return {
    // Reactive state
    isMounted,
    isIs24,

    // Computed properties
    priceTrendsAdaptData,
    series,
    evolutions,
    categories,
    min,
    max,
    selections,

    // Methods
    getXTextByIndex,
    handleRangeChangeHg,
    handleRangeChangeIs24,
    handleUpdatedRangeHg,
    handleUpdatedRangeIs24,

    // Constants
    NO_DATA_MIN,
    NO_DATA_MAX,
  };
}
